import { theme } from '../theme'
import { Ic, RecipeThumb } from '../icons'
import type { Meal, Recipe, SlotKey } from '../types'
import { SLOT_LABELS } from '../dates'

export function SlotCard({
  slotKey, meal, recipe, onClick, onClear, compact,
}: {
  slotKey: SlotKey
  meal?: Meal
  recipe?: Recipe
  onClick: () => void
  onClear?: () => void
  compact?: boolean
}) {
  if (!meal || !recipe) {
    return (
      <button onClick={onClick} style={{
        all: 'unset',
        display: 'flex', alignItems: 'center', gap: 8,
        width: '100%', minWidth: 0, boxSizing: 'border-box', overflow: 'hidden',
        padding: compact ? '8px 10px' : '10px 12px',
        height: compact ? 42 : 50,
        border: `1px dashed ${theme.lineStrong}`,
        borderRadius: theme.radius,
        background: 'transparent',
        color: theme.muted,
        fontFamily: theme.sans, fontSize: 12, letterSpacing: 0.2,
        cursor: 'pointer',
        transition: 'background 120ms, border-color 120ms, color 120ms',
      }}
        onMouseEnter={e => { e.currentTarget.style.background = theme.surface2; e.currentTarget.style.color = theme.inkSoft; e.currentTarget.style.borderColor = theme.ink }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = theme.muted; e.currentTarget.style.borderColor = theme.lineStrong }}
      >
        <Ic.plus width="14" height="14" style={{ flex: '0 0 auto' }} />
        <span style={{
          textTransform: 'uppercase', fontSize: 10, fontWeight: 600,
          minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{SLOT_LABELS[slotKey]}</span>
      </button>
    )
  }

  const status = meal.status
  return (
    <button onClick={onClick} style={{
      all: 'unset', boxSizing: 'border-box',
      display: 'flex', alignItems: 'center', gap: 10,
      width: '100%', minWidth: 0, overflow: 'hidden',
      padding: compact ? '7px 9px' : '9px 10px',
      height: compact ? 42 : 50,
      background: theme.surface,
      border: theme.cardBorder,
      borderRadius: theme.radius,
      boxShadow: theme.cardShadow,
      cursor: 'pointer',
      opacity: status === 'skipped' ? 0.55 : 1,
    }}>
      <div style={{ flex: '0 0 auto' }}>
        <RecipeThumb imageUrl={recipe.image_url} hue={recipe.hue} name={recipe.name} size={compact ? 28 : 32} radius={theme.radius} />
      </div>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div style={{
          fontFamily: theme.sans, fontSize: compact ? 12 : 13, fontWeight: 500,
          color: theme.ink, lineHeight: 1.2,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          textDecoration: status === 'skipped' ? 'line-through' : 'none',
        }}>{recipe.name}</div>
        <div style={{
          fontFamily: theme.sans, fontSize: 10, color: theme.muted,
          textTransform: 'uppercase', letterSpacing: 0.6, display: 'flex', alignItems: 'center', gap: 6,
          minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{SLOT_LABELS[slotKey]}</span>
          <span style={{ opacity: 0.5, flex: '0 0 auto' }}>·</span>
          <span style={{ flex: '0 0 auto' }}>{recipe.prep_minutes}m</span>
        </div>
      </div>
      {meal.from_pantry_item_id && (
        <span title="Leftovers — eats an on-hand recipe lot" style={{
          flex: '0 0 auto', fontFamily: theme.sans, fontSize: 9, fontWeight: 700,
          letterSpacing: 0.4, textTransform: 'uppercase', color: theme.surface,
          background: theme.accent, borderRadius: 999, padding: '1px 5px',
        }}>leftover</span>
      )}
      {meal.adjusted && (
        <span title="Adjusted for this meal" style={{
          flex: '0 0 auto', fontFamily: theme.sans, fontSize: 9, fontWeight: 700,
          letterSpacing: 0.4, textTransform: 'uppercase', color: theme.accentInk,
          border: `1px solid ${theme.line}`, borderRadius: 999, padding: '1px 5px',
        }}>tweaked</span>
      )}
      <StatusDot status={status} />
      {onClear && (
        <button
          onClick={e => { e.stopPropagation(); onClear() }}
          title="Remove from plan"
          style={{
            all: 'unset', cursor: 'pointer', flex: '0 0 auto',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 22, height: 22, borderRadius: 4,
            color: theme.muted, opacity: 0.5,
            transition: 'opacity 120ms, color 120ms',
          }}
          onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = '#b94040' }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '0.5'; e.currentTarget.style.color = theme.muted }}
        >
          <Ic.trash width="13" height="13" />
        </button>
      )}
    </button>
  )
}

export function StatusDot({ status }: { status: Meal['status'] }) {
  if (status === 'cooked') {
    return (
      <span title="Cooked" style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 14, height: 14, borderRadius: 999, background: theme.cooked, color: '#fff',
      }}>
        <Ic.check width="10" height="10"/>
      </span>
    )
  }
  if (status === 'skipped') {
    return <span title="Skipped" style={{
      display: 'inline-block', width: 14, height: 14, borderRadius: 999,
      border: `1.5px dashed ${theme.skipped}`, background: 'transparent',
    }}/>
  }
  return <span title="Planned" style={{
    display: 'inline-block', width: 8, height: 8, borderRadius: 999, background: theme.planned,
  }}/>
}
